import startCase from 'lodash/startCase'

import { AutomationsPresentationV1 } from 'Automations/automationEditor'

import { NodeType, ResolvedNodeDefinitions } from '../../../types'
import {
  NODE_VALIDATION_FIELD_ID,
  HANDLE_VALIDATION_FIELD_PREFIX,
  WORKFLOW_VALIDATION_KEY,
  WORKFLOW_VALIDATION_NOOP_FIELD
} from '../../constants'
import {
  ValidatorReturn,
  NodeValidation,
  NodeValidatorContext,
  ConfigValidationField,
  HandleValidation,
  ValidationResult,
  ValidationResults
} from '../../types'
import { isArrayValidation } from '../isArrayValidation'
import { getMissingUpstreamData } from '../getMissingUpstreamData'
import { isNoopWorkflow } from '../isNoopWorkflow'
import { getStartNode } from 'Automations/nodeDefinitions/helpers/getStartNode'

/**
 * Turns a camelCase semantic-input key into a human-readable label, e.g.
 * `billingSchedule` → `Billing schedule`. `startCase` splits and title-cases
 * the words; lower-casing then re-capitalising the first character yields
 * sentence case.
 */
const toSentenceCase = (value: string): string => {
  const words = startCase(value).toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const collectValidatorReturns = (
  result: ValidatorReturn | ValidatorReturn[] | undefined
): ValidatorReturn[] => {
  if (!result) return []
  return Array.isArray(result) ? result : [result]
}

const isValidatorReturn = (
  result: ValidatorReturn | undefined
): result is ValidatorReturn => Boolean(result)

export const validateFields = (
  data: Record<string, unknown>,
  validationConfig: Record<string, ConfigValidationField>,
  pathPrefix?: string,
  scope?: Record<string, unknown>
): Record<string, ValidationResult[]> => {
  return Object.keys(validationConfig).reduce<
    Record<string, ValidationResult[]>
  >((acc, fieldId) => {
    const fieldConfig = validationConfig[fieldId]
    const value = data[fieldId]
    const fieldPath = pathPrefix ? `${pathPrefix}.${fieldId}` : fieldId

    const errors = fieldConfig.ERROR.flatMap(validator =>
      collectValidatorReturns(validator(value, data, scope))
    )

    const warnings = fieldConfig.WARN.flatMap(validator =>
      collectValidatorReturns(validator(value, data, scope))
    )

    const fieldResults: Record<string, ValidationResult[]> = {
      [fieldPath]: [
        ...errors.map(result => ({ ...result, level: 'ERROR' as const })),
        ...warnings.map(result => ({ ...result, level: 'WARN' as const }))
      ]
    }

    const nestedResults =
      isArrayValidation(fieldConfig) && Array.isArray(value)
        ? value.reduce<Record<string, ValidationResult[]>>(
            (nestedAcc, item) => {
              const typedItem = item as Record<string, unknown>
              return {
                ...nestedAcc,
                ...validateFields(
                  typedItem,
                  fieldConfig.items,
                  `${fieldPath}.${typedItem.id}`,
                  scope
                )
              }
            },
            {}
          )
        : {}

    return { ...acc, ...fieldResults, ...nestedResults }
  }, {})
}

const validateNodeLevel = (
  context: NodeValidatorContext,
  nodeValidation: NodeValidation
): ValidationResult[] => {
  const errors = nodeValidation.ERROR.map(validator => validator(context))
    .filter(isValidatorReturn)
    .map(result => ({ ...result, level: 'ERROR' as const }))

  const warnings = nodeValidation.WARN.map(validator => validator(context))
    .filter(isValidatorReturn)
    .map(result => ({ ...result, level: 'WARN' as const }))

  return [...errors, ...warnings]
}

const validateHandles = (
  context: NodeValidatorContext,
  handleValidation: HandleValidation
): Record<string, ValidationResult[]> => {
  const errorResults = handleValidation.ERROR.flatMap(validator =>
    Object.entries(validator(context)).map(
      ([handleId, result]) =>
        [
          `${HANDLE_VALIDATION_FIELD_PREFIX}:${handleId}`,
          { ...result, level: 'ERROR' as const }
        ] as const
    )
  )

  const warnResults = handleValidation.WARN.flatMap(validator =>
    Object.entries(validator(context)).map(
      ([handleId, result]) =>
        [
          `${HANDLE_VALIDATION_FIELD_PREFIX}:${handleId}`,
          { ...result, level: 'WARN' as const }
        ] as const
    )
  )

  return [...errorResults, ...warnResults].reduce<
    Record<string, ValidationResult[]>
  >((acc, [key, result]) => {
    return { ...acc, [key]: [...(acc[key] ?? []), result] }
  }, {})
}

/**
 * Workflow-level validation results — checks that belong to the whole design
 * rather than a single node. The generatability check is deliberately not here:
 * running it on every edit is costly, so it is performed once at publish time
 * (see the publish flow) and surfaced as a toast rather than in this display.
 *
 * The no-op check is gated on a configured start for the same reason as the
 * per-node upstream-data check: a workflow that is still just a placeholder
 * start is already blocked by that node's own validation, so flagging it as
 * empty too would be redundant noise while the user is still building.
 */
const validateWorkflow = (
  presentation: AutomationsPresentationV1,
  hasConfiguredStart: boolean
): Record<string, ValidationResult[]> => {
  const noopResults: ValidationResult[] =
    hasConfiguredStart && isNoopWorkflow(presentation)
      ? [
          {
            title: 'Empty workflow',
            message: 'Add at least one action or review before publishing',
            level: 'ERROR'
          }
        ]
      : []

  return {
    [WORKFLOW_VALIDATION_NOOP_FIELD]: noopResults
  }
}

export const validatePresentation = (
  presentation: AutomationsPresentationV1,
  definitions: ResolvedNodeDefinitions
): ValidationResults => {
  // Upstream-data checks are only meaningful once a start node is configured:
  // until then every downstream node is trivially "missing" data because the
  // (placeholder) start produces no outputs. The placeholder's own validation
  // already blocks that state, so skipping avoids duplicate, transient noise.
  const hasConfiguredStart = Boolean(getStartNode(presentation, definitions))

  const perNode = presentation.nodes.map(node => {
    if (!node.type) throw new Error('Unable to get node definition')
    const definition = definitions[node.type as NodeType]

    const context: NodeValidatorContext = {
      node,
      definition,
      edges: presentation.edges,
      nodes: presentation.nodes
    }

    const scope = definition.buildValidationScope?.({
      node,
      presentation,
      definitions
    })

    const fieldResults = validateFields(
      node.data as Record<string, unknown>,
      definition.validation.config,
      undefined,
      scope
    )

    const nodeResults = validateNodeLevel(context, definition.validation.node)

    const handleResults = validateHandles(
      context,
      definition.validation.handles
    )

    const missingUpstreamData = hasConfiguredStart
      ? getMissingUpstreamData({
          node,
          nodes: presentation.nodes,
          edges: presentation.edges,
          definitions
        })
      : []

    // The errors popover renders each result as "{title}: {message}", so the
    // title carries the label and the message the offending properties (in
    // human-readable sentence case) — together reading as e.g. "Missing
    // upstream data: Billing schedule, Contract".
    const upstreamDataResults: ValidationResult[] =
      missingUpstreamData.length > 0
        ? [
            {
              title: 'Missing upstream data',
              message: missingUpstreamData.map(toSentenceCase).join(', '),
              level: 'ERROR'
            }
          ]
        : []

    return [
      node.id,
      {
        ...fieldResults,
        ...handleResults,
        [NODE_VALIDATION_FIELD_ID]: [...nodeResults, ...upstreamDataResults]
      }
    ] as const
  })

  return {
    [WORKFLOW_VALIDATION_KEY]: validateWorkflow(
      presentation,
      hasConfiguredStart
    ),
    ...Object.fromEntries(perNode)
  }
}
